/**
 * SRES · CPR Preview Modal (Iter98k · R7 · Feb 2026).
 *
 * Loads the compiled CPR PDF for a report run and displays it in an
 * embedded iframe so the CP can review the layout before hitting
 * Publish. Uses the existing `/api/projects/{pid}/report-runs/{run_id}/pdf`
 * artifact endpoint — no new backend surface.
 *
 * Renders a signed blob URL so the browser's built-in PDF viewer can
 * read the bytes without triggering a download.
 */
import { useEffect, useMemo, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  FileText, ArrowsClockwise, DownloadSimple,
  ArrowsOut, ArrowsIn, X,
  MagnifyingGlassPlus, MagnifyingGlassMinus, CaretLeft, CaretRight,
} from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";


// Iter114 · Zoom presets that map to the PDF viewer's #zoom= fragment.
// Chromium's built-in PDF viewer honours numeric zoom (percent) plus
// the special value "page-width". We keep the stops small so tapping
// +/− feels responsive on tablets.
const ZOOM_STOPS = [50, 75, 100, 125, 150, 200, 300];


export default function CPRPreviewModal({ pid, runId, open, onClose,
                                             onDownloadFallback }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [status,  setStatus]  = useState("idle");   // idle | loading | ready | error
  const [error,   setError]   = useState(null);
  const [size,    setSize]    = useState(0);
  // Iter113 · Feb 2026 · Full-screen "reading mode" for CPs on tablets.
  // Local to the modal so it doesn't touch the wider cinema-mode
  // preference. Reset every time the modal opens.
  const [fullscreen, setFullscreen] = useState(false);
  // Iter114 · Feb 2026 · Zoom + page-jump controls layered on top of
  // the browser's built-in PDF viewer. Chromium honours the
  // `#page=N&zoom=Z` URL fragment, so we simply rebuild the iframe
  // src whenever the user clicks +/− or Prev/Next page.
  const [zoom,     setZoom]     = useState(100);       // percent
  const [page,     setPage]     = useState(1);
  useEffect(() => { if (!open) { setFullscreen(false); setZoom(100); setPage(1); } }, [open]);
  const framedUrl = useMemo(() => {
    if (!blobUrl) return null;
    return `${blobUrl}#page=${Math.max(1, page)}&zoom=${zoom}`;
  }, [blobUrl, page, zoom]);
  const zoomOut = () => {
    const cur = ZOOM_STOPS.findIndex((s) => s >= zoom);
    setZoom(ZOOM_STOPS[Math.max(0, cur - 1)] || 50);
  };
  const zoomIn  = () => {
    const cur = ZOOM_STOPS.findIndex((s) => s >= zoom);
    setZoom(ZOOM_STOPS[Math.min(ZOOM_STOPS.length - 1, cur + 1)] || 300);
  };
  const prevPage = () => setPage((p) => Math.max(1, p - 1));
  const nextPage = () => setPage((p) => p + 1);

  useEffect(() => {
    if (!open || !pid || !runId) return;
    let cancelled = false;
    let localUrl  = null;
    setStatus("loading"); setError(null); setBlobUrl(null); setSize(0);
    (async () => {
      try {
        const res = await api.get(
          `/projects/${pid}/report-runs/${runId}/pdf`,
          { responseType: "blob", timeout: 90_000 },
        );
        if (cancelled) return;
        localUrl = URL.createObjectURL(res.data);
        setBlobUrl(localUrl);
        setSize(res.data.size || 0);
        setStatus("ready");
      } catch (e) {
        if (cancelled) return;
        setError(e?.response?.data?.detail || e.message || "Preview failed");
        setStatus("error");
      }
    })();
    return () => {
      cancelled = true;
      if (localUrl) URL.revokeObjectURL(localUrl);
    };
  }, [open, pid, runId]);

  const download = () => {
    if (!blobUrl) return onDownloadFallback?.();
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = `cpr_${runId}.pdf`;
    document.body.appendChild(a); a.click(); a.remove();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className={fullscreen
          ? "!max-w-none w-screen h-[100dvh] !p-0 !rounded-none !border-0 overflow-hidden"
          : "max-w-5xl w-full max-h-[92vh] p-0 overflow-hidden"}
        data-testid="cpr-preview-modal"
        data-cpr-preview-fullscreen={fullscreen ? "true" : "false"}>
        {/* Compact header — in full-screen mode it collapses to a
            floating action cluster so nothing distracts the CP. */}
        {fullscreen ? (
          <div className="absolute top-3 right-3 z-10 flex items-center gap-1.5 flex-wrap justify-end"
                    style={{ top: "calc(0.75rem + env(safe-area-inset-top))" }}>
            {/* Iter114 · Zoom cluster */}
            <div className="flex items-center gap-0.5 bg-white border border-slate-200 rounded-md shadow-sm p-0.5"
                      data-testid="cpr-preview-zoom-cluster-fs">
              <button type="button" onClick={zoomOut}
                          disabled={zoom <= ZOOM_STOPS[0]}
                          aria-label="Zoom out"
                          data-testid="cpr-preview-zoom-out-fs"
                          className="w-7 h-7 rounded text-slate-600 hover:text-brand-blue disabled:opacity-40 flex items-center justify-center transition">
                <MagnifyingGlassMinus size={13} weight="bold" />
              </button>
              <span className="min-w-[40px] text-center font-mono text-[11px] text-slate-700"
                        data-testid="cpr-preview-zoom-value-fs">{zoom}%</span>
              <button type="button" onClick={zoomIn}
                          disabled={zoom >= ZOOM_STOPS[ZOOM_STOPS.length - 1]}
                          aria-label="Zoom in"
                          data-testid="cpr-preview-zoom-in-fs"
                          className="w-7 h-7 rounded text-slate-600 hover:text-brand-blue disabled:opacity-40 flex items-center justify-center transition">
                <MagnifyingGlassPlus size={13} weight="bold" />
              </button>
            </div>
            {/* Iter114 · Page cluster */}
            <div className="flex items-center gap-0.5 bg-white border border-slate-200 rounded-md shadow-sm p-0.5"
                      data-testid="cpr-preview-page-cluster-fs">
              <button type="button" onClick={prevPage}
                          disabled={page <= 1}
                          aria-label="Previous page"
                          data-testid="cpr-preview-prev-page-fs"
                          className="w-7 h-7 rounded text-slate-600 hover:text-brand-blue disabled:opacity-40 flex items-center justify-center transition">
                <CaretLeft size={12} weight="bold" />
              </button>
              <span className="min-w-[36px] text-center font-mono text-[11px] text-slate-700"
                        data-testid="cpr-preview-page-value-fs">p{page}</span>
              <button type="button" onClick={nextPage}
                          aria-label="Next page"
                          data-testid="cpr-preview-next-page-fs"
                          className="w-7 h-7 rounded text-slate-600 hover:text-brand-blue flex items-center justify-center transition">
                <CaretRight size={12} weight="bold" />
              </button>
            </div>
            <Button size="sm" onClick={download}
                        disabled={status !== "ready"}
                        className="bg-brand-blue hover:bg-brand-blue/90 text-white text-[11.5px]"
                        data-testid="cpr-preview-download-btn">
              <DownloadSimple size={12} className="mr-1" />
              Download
            </Button>
            <button type="button"
                        onClick={() => setFullscreen(false)}
                        aria-label="Exit full-screen"
                        data-testid="cpr-preview-exit-fullscreen"
                        className="w-8 h-8 rounded-md bg-white border border-slate-200
                                      text-slate-600 hover:text-brand-blue hover:border-brand-blue
                                      flex items-center justify-center transition shadow-sm">
              <ArrowsIn size={13} weight="bold" />
            </button>
            <button type="button"
                        onClick={onClose}
                        aria-label="Close preview"
                        data-testid="cpr-preview-close-fullscreen"
                        className="w-8 h-8 rounded-md bg-white border border-slate-200
                                      text-slate-600 hover:text-rose-600 hover:border-rose-300
                                      flex items-center justify-center transition shadow-sm">
              <X size={13} weight="bold" />
            </button>
          </div>
        ) : (
          <DialogHeader className="px-5 py-3 border-b border-slate-200 flex-row items-center gap-2 flex-wrap">
            <DialogTitle className="flex items-center gap-2 text-brand-blue">
              <FileText size={18} /> CPR PDF Preview
            </DialogTitle>
            {size > 0 && (
              <span className="text-[11px] font-mono text-slate-500"
                        data-testid="cpr-preview-size">
                {(size / 1024 / 1024).toFixed(2)} MB
              </span>
            )}
            <div className="flex-1" />
            {/* Iter114 · Zoom cluster */}
            <div className="flex items-center gap-0.5 border border-slate-200 rounded-md p-0.5"
                      data-testid="cpr-preview-zoom-cluster">
              <button type="button" onClick={zoomOut}
                          disabled={zoom <= ZOOM_STOPS[0]}
                          aria-label="Zoom out"
                          data-testid="cpr-preview-zoom-out"
                          className="w-7 h-7 rounded text-slate-600 hover:text-brand-blue disabled:opacity-40 flex items-center justify-center transition">
                <MagnifyingGlassMinus size={12} weight="bold" />
              </button>
              <span className="min-w-[40px] text-center font-mono text-[10.5px] text-slate-700"
                        data-testid="cpr-preview-zoom-value">{zoom}%</span>
              <button type="button" onClick={zoomIn}
                          disabled={zoom >= ZOOM_STOPS[ZOOM_STOPS.length - 1]}
                          aria-label="Zoom in"
                          data-testid="cpr-preview-zoom-in"
                          className="w-7 h-7 rounded text-slate-600 hover:text-brand-blue disabled:opacity-40 flex items-center justify-center transition">
                <MagnifyingGlassPlus size={12} weight="bold" />
              </button>
            </div>
            {/* Iter114 · Page cluster */}
            <div className="flex items-center gap-0.5 border border-slate-200 rounded-md p-0.5"
                      data-testid="cpr-preview-page-cluster">
              <button type="button" onClick={prevPage}
                          disabled={page <= 1}
                          aria-label="Previous page"
                          data-testid="cpr-preview-prev-page"
                          className="w-7 h-7 rounded text-slate-600 hover:text-brand-blue disabled:opacity-40 flex items-center justify-center transition">
                <CaretLeft size={11} weight="bold" />
              </button>
              <span className="min-w-[36px] text-center font-mono text-[10.5px] text-slate-700"
                        data-testid="cpr-preview-page-value">p{page}</span>
              <button type="button" onClick={nextPage}
                          aria-label="Next page"
                          data-testid="cpr-preview-next-page"
                          className="w-7 h-7 rounded text-slate-600 hover:text-brand-blue flex items-center justify-center transition">
                <CaretRight size={11} weight="bold" />
              </button>
            </div>
            <Button size="sm" onClick={download}
                      disabled={status !== "ready"}
                      className="bg-brand-blue hover:bg-brand-blue/90 text-white text-[11.5px]"
                      data-testid="cpr-preview-download-btn">
              <DownloadSimple size={12} className="mr-1" />
              Download
            </Button>
            {/* Iter113 · Enter full-screen reading mode */}
            <button type="button"
                        onClick={() => setFullscreen(true)}
                        title="Full-screen reading mode"
                        aria-label="Enter full-screen preview"
                        data-testid="cpr-preview-enter-fullscreen"
                        className="w-8 h-8 rounded-md border border-slate-200 text-slate-500
                                      hover:text-brand-blue hover:border-brand-blue
                                      flex items-center justify-center transition">
              <ArrowsOut size={13} weight="bold" />
            </button>
          </DialogHeader>
        )}

        <div className={fullscreen ? "w-full h-full bg-slate-100" : "w-full h-[80vh] bg-slate-100"}>
          {status === "loading" && (
            <div className="w-full h-full flex flex-col items-center justify-center gap-2 text-slate-500"
                     data-testid="cpr-preview-loading">
              <ArrowsClockwise size={22} className="animate-spin text-brand-blue" />
              <div className="text-[12px]">Fetching compiled CPR PDF…</div>
            </div>
          )}
          {status === "error" && (
            <div className="w-full h-full flex flex-col items-center justify-center gap-2 text-rose-700 p-6 text-center"
                     data-testid="cpr-preview-error">
              <div className="font-bold">Preview failed</div>
              <div className="text-[12px] max-w-md">{error}</div>
              <div className="text-[11px] text-slate-500 mt-2">
                Tip: This report run may not be in a `ready` state yet —
                confirm the compile has finished, then re-open the preview.
              </div>
            </div>
          )}
          {status === "ready" && framedUrl && (
            <iframe
              src={framedUrl}
              title="CPR PDF preview"
              className="w-full h-full border-0"
              data-testid="cpr-preview-iframe"
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
